import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { rpcClient, orpc } from '@/api/rpc/client';
import { useToast } from '@/context/ToastContext';

interface BulkCreatePaymentsResult {
  paymentsCreated: number;
  invoicesProcessed: number;
  failures: { invoiceId: string; error: string }[];
}

export interface BulkCreatePaymentOptions {
  invoiceIds: string[];
  scheduledDates?: Record<string, string>;
}

export function useBulkCreatePaymentsAPInvoices() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const bulkCreatePayments = useCallback(
    async (options: BulkCreatePaymentOptions) => {
      const { invoiceIds, scheduledDates } = options;
      if (invoiceIds.length === 0) return;

      setIsLoading(true);

      let result: BulkCreatePaymentsResult | undefined;

      try {
        result = await rpcClient.accountsPayable.invoices.bulkCreatePayments({
          invoiceIds,
          scheduledDates,
        });
      } catch (error) {
        showToast({
          title: 'Bulk Create Payments Failed',
          message:
            error instanceof Error ? error.message : 'Unknown error occurred',
          color: 'error',
          icon: 'error',
        });
        setIsLoading(false);
        return;
      }

      setIsLoading(false);

      if (result.failures.length === 0) {
        showToast({
          title: 'Payments Created',
          message: `Created ${result.paymentsCreated} payment${result.paymentsCreated !== 1 ? 's' : ''} for ${result.invoicesProcessed} invoice${result.invoicesProcessed !== 1 ? 's' : ''}`,
          color: 'success',
          icon: 'check',
        });
      } else if (result.invoicesProcessed === 0) {
        showToast({
          title: 'Bulk Create Payments Failed',
          message: `Failed to create payments for all ${result.failures.length} invoice${result.failures.length !== 1 ? 's' : ''}`,
          color: 'error',
          icon: 'error',
        });
      } else {
        showToast({
          title: 'Payments Partially Created',
          message: `Created ${result.paymentsCreated} payment${result.paymentsCreated !== 1 ? 's' : ''} for ${result.invoicesProcessed} invoice${result.invoicesProcessed !== 1 ? 's' : ''}. ${result.failures.length} failed.`,
          color: 'warning',
          icon: 'warning',
        });
      }

      queryClient.invalidateQueries({
        queryKey: orpc.accountsPayable.invoices.list.queryKey({ input: {} }),
      });

      return result;
    },
    [queryClient, showToast],
  );

  return {
    bulkCreatePayments,
    isLoading,
  };
}
